// components/meetings/MeetingSeriesTable.tsx
import type { ColDef, GridOptions } from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import React, { useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";

import { Anchor } from "@mantine/core";
import { IMeeting } from "@mctypes/meeting.types";
import { AG_GRID_LOCALE_NOR } from "../../../../agGridLocale";
import { Avatar, FailedAlert, MCLoader, McTitle } from "../../../../components";
import { CellEditFormatter } from "../../../../components/columnGlobal";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { getProjectIdFromUrl } from "../../../utils";
import { formatShortDate } from "../../admin/selectors";
import { MEETINGS_SERIES_STATUSES } from "../../projectdashboard/MeetingSeries/meetingSeriesConstants";
import MeetingBoardsTable from "../MeetingBoards/MeetingBoardsTable";
import { useGetMeetingSeriesQuery } from "../meetingSerieRTK";
import { setClickedMeetingSerieIdInRedux } from "../meetingSlice";
import MeetingSerieItemModal from "./MeetingSerieItemModal";

type Row = IMeeting.MeetingSeriePopulated;

export const MeetingSeriesTable: React.FC<{ search: string }> = ({ search }) => {
  const hideDeletedMeetingSeries = useAppSelector((s) => s.meetingReducer.hideDeletedMeetingSeries);
  const clickedMeetingSerieId = useAppSelector((s) => s.meetingReducer.clickedMeetingSerieId);

  const dispatch = useAppDispatch();
  const history = useHistory();
  const gridRef = useRef<AgGridReact<Row>>(null);

  const projectId = getProjectIdFromUrl(history.location.pathname);

  const { data = [], isLoading, isFetching, error } = useGetMeetingSeriesQuery({ projectId });

  // Behold rader under refetch
  const rows: Row[] = useMemo(() => {
    if (!data) return [];
    return hideDeletedMeetingSeries ? data.filter((r) => r.status !== "ARCHIVED" && r.status !== "DELETED") : data;
  }, [data, hideDeletedMeetingSeries]);

  const handleOpenSerie = useCallback((id: string) => dispatch(setClickedMeetingSerieIdInRedux(id)), [dispatch]);

  const columns = useMemo<ColDef<Row>[]>(() => {
    return [
      {
        headerName: "",
        type: "metaColumn",
        width: 40,
        maxWidth: 40,
        pinned: "left",
        resizable: false,
        suppressSizeToFit: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
        cellStyle: { padding: "0px" },
        cellRenderer: (props) => {
          const { node, api } = props;
          const onClick = (e: React.MouseEvent) => {
            e.preventDefault();
            node.setExpanded(!node.expanded);
            api.refreshCells();
            api.setFocusedCell(node.rowIndex!, "id", null as any);
          };
          return (
            <div onClick={onClick} className={`expandIcon${node.expanded ? " rotateIcon" : ""}`}>
              <i className="fa fa-angle-down" />
            </div>
          );
        },
      },
      { field: "_id", headerName: "_id", hide: true },
      {
        field: "name",
        headerName: "Tittel",
        flex: 1,
        minWidth: 300,
        filter: "agTextColumnFilter",
        editable: false,
        autoHeight: true,
        cellRenderer: (p) => (
          <Anchor fw={500} onClick={() => handleOpenSerie(p.data._id)}>
            {String(p.value ?? "").toUpperCase()}
          </Anchor>
        ),
      },
      {
        field: "description",
        headerName: "Beskrivelse",
        minWidth: 250,
        flex: 1,
        filter: "agTextColumnFilter",
        editable: false,
        cellRenderer: (p) => CellEditFormatter(p.value),
      },
      {
        field: "createdBy",
        headerName: "Opprettet av",
        filter: "agTextColumnFilter",
        editable: false,
        valueGetter: (p) => p.data?.createdBy, // forventer { _id, name }
        cellRenderer: (p) => {
          const owner = p.value as { _id?: string; name?: string } | undefined;
          if (!owner) return <div />;
          return (
            <div className="d-flex align-items-center">
              {owner._id && <Avatar id={owner._id} name={owner.name} />}
              <div className="ml-2">{owner.name ?? ""}</div>
            </div>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Opprettet",
        width: 160,
        filter: "agDateColumnFilter",
        editable: false,
        sort: "desc",
        cellRenderer: (p) => formatShortDate(p.value),
      },
      {
        field: "status",
        headerName: "Status",
        filter: "agTextColumnFilter",
        editable: false,
        cellRenderer: (p) => MEETINGS_SERIES_STATUSES.find((s) => s.value === p.value)?.label ?? p.value,
      },
    ];
  }, [handleOpenSerie]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      suppressHeaderMenuButton: true,
      minWidth: 140,
      resizable: true,
      editable: false,
      sortable: true,
      filter: true,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
        wordBreak: "break-word",
        padding: "0.6em 0.8em",
      },
    }),
    [],
  );

  const getRowId = useCallback((p: { data: Row }) => p.data._id, []);
  const gridOptions = useMemo<GridOptions<Row>>(
    () => ({
      maintainColumnOrder: true,
      suppressColumnMoveAnimation: true,
      masterDetail: true,
      detailRowAutoHeight: true,
    }),
    [],
  );

  // Detail renderer
  const DetailCellRenderer = useCallback(
    ({ data }: CustomCellRendererProps<Row>) => (
      <McTitle className="p-2" title="Møteforekomster i serie">
        <MeetingBoardsTable autoHeight singleMeetingId={data!._id} />
      </McTitle>
    ),
    [],
  );

  return (
    <div className="d-flex flex-column h-100 w-100">
      {clickedMeetingSerieId && (
        <MeetingSerieItemModal
          toggle={() => dispatch(setClickedMeetingSerieIdInRedux(null))}
          meetingSerie={data?.find((x) => x._id === clickedMeetingSerieId)}
        />
      )}

      {isLoading && <MCLoader loading />}
      {!!error && <FailedAlert errorMessage="Kunne ikke laste inn møteserier" />}

      {!error && (
        <div className="ag-theme-quartz h-100 w-100">
          <AgGridReact<Row>
            animateRows={false}
            ref={gridRef}
            rowData={rows}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            gridOptions={gridOptions}
            getRowId={getRowId}
            suppressClickEdit
            suppressHeaderFocus
            cellSelection
            quickFilterText={search}
            localeText={AG_GRID_LOCALE_NOR}
            detailCellRenderer={DetailCellRenderer as any}
            onGridReady={(e) => e.api.autoSizeColumns(["name", "status", "createdAt"])}
          />
        </div>
      )}
    </div>
  );
};
